import { HttpClient, HttpContext, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, map, of, switchMap, tap } from 'rxjs';
import { environment } from 'src/environments/environment';
import { LoginData } from '../interfaces/Iip-login';
import { User } from '../interfaces/Iip-user';
import { IpRoleService } from './ip-role.service';
import { RoleResponse } from '../interfaces/ip-role-response';
import { SKIP_LOADING } from 'src/app/shared/interceptors/loading.interceptor';
import { IpValidUsernameResponse } from '../interfaces/ip-valid-username-response';

@Injectable({
  providedIn: 'root',
})
export class IpAuthService {
  api = environment.api;
  private sessionSubject: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(false);
  public currentSession$: Observable<boolean> =
    this.sessionSubject.asObservable();
  private userRole?: RoleResponse;
  private otpSessionId: string | null = null;

  constructor(
    private httpClient: HttpClient,
    private router: Router,
    private ipRoleService: IpRoleService,
    private dialogRef: MatDialog
  ) {
    if (this.getToken()) {
      this.sessionSubject.next(true);
    }
  }

  public login(
    loginUser: LoginData,
    isAdminLogin: boolean = false
  ): Observable<User> {
    const endpoint = isAdminLogin
      ? `${this.api}/invopay/users/signIn`
      : `${this.api}/invopay/users/signIn/employee`;
    return this.httpClient
      .post<User>(endpoint, loginUser)
      .pipe(
        map((resp) => this.mapUser(resp)),
        switchMap((user) => this.loadPermissions(user))
      );
  }

  private loadPermissions(user: User): Observable<User> {
    if (user.roleResponse){
      return of(user);
    }
    return this.ipRoleService.getAllPermissionsByEnterpriseAndRole().pipe(
      tap((permissions) => this.ipRoleService.setPermissions(permissions)),
      map(() => user)
    );
  }

  public getOtpSessionId(): string | null {
    return this.otpSessionId;
  }

  private mapUser(resp: User) {
    if (resp.requires2fa && resp.otpSessionId) {
      this.otpSessionId = resp.otpSessionId;
      return resp;
    }
    if (resp.access_token) {
      // TODO: remove this line:
      // Just in case:
      sessionStorage.clear()
      // It's important to set 'path'
      // Otherwise, they won't be deleted afterwards
      sessionStorage.setItem('access_token', resp.access_token);
      sessionStorage.setItem('username', resp.username);
      sessionStorage.setItem('fullName', resp.fullName ?? '');
      sessionStorage.setItem('userType', resp.userType);
      sessionStorage.setItem('userStatus', resp.userStatus ?? '');
      if(resp.supplierSignInReponse){
        sessionStorage.setItem('isBroker', resp.supplierSignInReponse.isBroker);
      }
      if(resp.roleResponse){
        this.setUserRole(resp.roleResponse)
      }
      this.sessionSubject.next(true);
      return resp;
    }
    throw new Error('Missing token.');
  }

  public getToken() {
    return sessionStorage.getItem('access_token') ?? false;
  }

  public logOut() {
    if (this.getToken()) {
      // This checks wether the user was logged in a an employee
      // Why? So that they get redirected back to employees' login
      // and not to admins' login
      const wasEmployee = this.ipRoleService.getType() === 'employee';
      let wasSupplier = false;
      let wasBroker = false;
      if ( this.ipRoleService.getType() === 'provider' ){
        wasSupplier = !this.ipRoleService.getIsBroker()
        wasBroker = this.ipRoleService.getIsBroker()
      }
      sessionStorage.clear()
      this.dialogRef.closeAll();
      this.sessionSubject.next(false);
      if (wasEmployee) {
        this.router.navigateByUrl('/invopay/admin/login-employee');
      } else if (wasSupplier){
        this.router.navigateByUrl('/invopay/admin/login-supplier');
      } else if (wasBroker){
        this.router.navigateByUrl('/invopay/admin/login-broker');
      } else {
        this.router.navigateByUrl('/invopay/admin/login-admin');
      }
    }
  }

  public sendResetPasswordEmail(email: string) {
    return this.httpClient.get(
      `${this.api}/invopay/users/reset-password-email`,
      { params: { email } }
    );
  }

  public resetPassword(newPassword: string, token: string, captchaToken: string|null=null): Observable<User> {
    return this.httpClient.post<User>(`${this.api}/invopay/users/reset-password`, {
      newPassword, token, captchaToken
    }).pipe(
      map((resp) => this.mapUser(resp)),
      switchMap((user) => this.loadPermissions(user))
    );
  }

  public getUserRole(){
    return this.userRole
  }
  
  public setUserRole(userRole:RoleResponse){
    this.userRole = userRole
  }

  // public authWithJwt(
  //   jwt: string
  // ): Observable<User> {
  //     return this.httpClient
  //       .post<User>(`${this.api}/invopay/users/signInWithJwt/${jwt}`, jwt)
  //       .pipe(map((resp) => this.mapUser(resp)));
  // }

  public authWithJwt(
    jwt: string
  ): Observable<User> {
    const headers = new HttpHeaders({
      'jwt': jwt
    });
    return this.httpClient
      .post<User>(`${this.api}/invopay/users/signInWithJwt`, jwt, {headers})
      .pipe(
        map((resp) => this.mapUser(resp)),
        switchMap((user) => this.loadPermissions(user))
      );
  }
  
  public getSecurityKey(
    enterpriseId: number
  ): Observable<string> {
    const headers = new HttpHeaders({
      'enterpriseId': enterpriseId
    });
    return this.httpClient
      .post<string>(`${this.api}/invopay/users/securityKey`, null, {headers})
  }
  
  public getTokenFromSecurityKey(
    securityKey: string,
    email: string
  ): Observable<string> {
    const headers = new HttpHeaders({
      'securityKey': securityKey,
      'email': email
    });
    return this.httpClient
      .post<string>(`${this.api}/invopay/users/tokenFromSecurityKey`, null, {headers})
  }

  // public getHeaders(url: string) {
  //     return this.httpClient.get(url, { observe: 'response' });
  //     }

  public checkUsernameAvailability(username: string): Observable<IpValidUsernameResponse> {
    return this.httpClient
      .get<IpValidUsernameResponse>(`${this.api}/invopay/users/validate-username`, {
        params: { username },
        context: new HttpContext().set(SKIP_LOADING, true)
      });
  }

  public isOnlyReadMode(): boolean {
    const status = sessionStorage.getItem('userStatus'); 
    return status ? status === 'INACTIVE' : false;
  }

  public verifyOtp(code: string): Observable<User> {
    const sessionId = this.otpSessionId;
    if (!sessionId) {
      throw new Error('No OTP session active.');
    }
    return this.httpClient.post<User>(`${this.api}/invopay/users/verify-otp`, { sessionId, code }).pipe(
      tap(() => (this.otpSessionId = null)),
      map((resp) => this.mapUser(resp)),
      switchMap((user) => this.loadPermissions(user))
    );
  }
}
